import { apiClient } from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";

export type ReferralStatusFilter = string | "all" | "pending" | "completed";

export type AffiliateApiResponse<T> = {
  status: "success" | "error";
  message: string;
  data: T;
};

export type ReferralItem = {
  id?: string | number;
  referal_user_id?: number;
  refered_user_id?: number;
  referal_code?: string;
  rewards_points?: number;
  signup_at?: string;
  completed_at?: string | null;
  first_protest_at?: string | null;
  type?: string;
  parent_affiliate_id?: string | number | null;
  updated_at?: string;
  title?: string;
  message?: string;
  name?: string;
  email?: string;
  sharedWith?: string;
  status: string;
  created_at?: string;
  rewardPointsAwarded?: number;
  referal_user?: {
    id?: number;
    first_name: string;
    last_name?: string;
    email: string;
  };
  refered_user?: {
    id?: number;
    first_name: string;
    last_name?: string;
    email: string;
  };
};

export type ReferralStatistics = {
  totalPointsUsed: number;
};

export type ReferralTransaction = {
  id: string;
  type: "points_used" | "points_earned";
  pointsUsed?: number;
  pointsEarned?: number;
  propertyAddress?: string;
  reason?: string;
  createdAt: string;
};

export type ReferralLinkData = {
  id: string;
  referralLink: string;
  referralCode: string;
  status: "pending" | "approved";
  userId: string;
  email: string;
  name: string;
  role: string;
  createdAt: string;
};

export type ReferralDataResponse = {
  referral_code: string | null;
  referral_link: string | null;
  referral_link_data?: ReferralLinkData | null;
  total_referrals: number | null;
  confirmed_referrals?: number | null;
  total_referred_users_by_role?: {
    affiliate: number;
    user: number;
  };
  total_credits: number | null;
  is_admin_approve: boolean;
  is_user_requested: boolean | null;
};

export type GenerateReferralCodeResponse = {
  referralLink: string;
  referralCode: string;
  status: "pending" | "approved";
};

export type ReferralHistoryResponse = {
  referrals: ReferralItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
};

export type MyReferralsResponse = {
  referrals: ReferralItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
};

export type GetReferralHistoryPayload = {
  page: number;
  limit: number;
};

export type MyReferralsPayload = {
  page: number;
  limit: number;
  role: string;
  status: string;
  search: string;
};

export const referralService = {
  async getReferralData(): Promise<AffiliateApiResponse<ReferralDataResponse>> {
    const { data } = await apiClient.get<AffiliateApiResponse<ReferralDataResponse>>(
      API_ENDPOINTS.AFFILIATE.OVERVIEW
    );
    return data;
  },

  async generateReferralCode(): Promise<AffiliateApiResponse<GenerateReferralCodeResponse>> {
    const { data } = await apiClient.put<AffiliateApiResponse<GenerateReferralCodeResponse>>(
      API_ENDPOINTS.AFFILIATE.REQUEST_AFFILIATE
    );
    return data;
  },

  async getReferralHistory(
    payload: GetReferralHistoryPayload
  ): Promise<AffiliateApiResponse<ReferralHistoryResponse>> {
    const { data } = await apiClient.post<AffiliateApiResponse<ReferralHistoryResponse>>(
      API_ENDPOINTS.AFFILIATE.REFERRAL_HISTORY,
      payload
    );
    return data;
  },

  async myReferrals(
    payload: MyReferralsPayload
  ): Promise<AffiliateApiResponse<MyReferralsResponse>> {
    const { data } = await apiClient.post<AffiliateApiResponse<MyReferralsResponse>>(
      API_ENDPOINTS.AFFILIATE.REFERRAL_LIST,
      payload
    );
    return data;
  },
};
